import _ from 'lodash';
import io from "socket.io-client";
import { useOrders } from "../store/orders";
import useProducerLocations from '../store/producer-locations';
import useSocketStatus from "../store/socket-status";
import useUser from "../store/user";
import {
  clearLocalStorage,
  getTokenFromLocalStorage,
  getUserFromLocalStorage,
} from "./local-storage";
import { logout } from "./user";
const { Bouncer } = require("@relay/rabbitmq-sockets-client");

let bouncer;
export const socket = io(
  `${process.env.REACT_APP_RELAY_PARTNER_SOCKET_HOST}/admin`,
  {
    query: { version: "0.0.0" },
    forceNew: true,
    transports: ["websocket"],
    autoConnect: false,
    upgrade: false,
  }
);

export enum SocketEvent {
  Connect = "connect",
  Disconnect = "disconnect",
  Error = "error",
  ConnectError = "connect_error",
  InvalidToken = "invalid-token",
  OrderChange = "order-change",
  RestaurantDispatcherSettingsChanged = 'restaurant-dispatcher-settings-changed',
  ActiveOrders = "active-orders",
  RiderSnappedPing = "rider-snapped-ping",
  JobFullPath = "job-full-path",
  JobRiderPings = "job-rider-pings",
  RiderEstimatedDropoff = "rider-estimated-dropoff",
}

export enum SocketRPC {
  JoinV2 = "join-v2",
  LeaveV2 = "leave-v2",
  GetActiveOrders = "get-active-orders",
  GetTrackingURL = "get-tracking-url",
  TryOrderUpdate = "try-order-update",
  GetJobFullPath = "get-job-full-path",
  GetJobRiderPings = "get-job-rider-pings",
  GetS3URL = "get-s3-url",
  GetSuperAdminUsers = "get-super-admin-users",
  GetSuperAdminRoles = "get-super-admin-roles",
  UpdateSuperAdminUserRoles = "update-super-admin-user-roles",
  InviteSuperAdminUser = "invite-super-admin-user",
  DeleteSuperAdminUser = "delete-super-admin-user",
  GetSuperAdminInvites = "get-super-admin-invites",
  BumpSuperAdminInvite = "bump-super-admin-invite",
  CancelSuperAdminInvite = "cancel-super-admin-invite",
  UpdateUser = "user-update-password",
  GetOrderHistory = "get-order-history",
  SearchOrderHistory = "search-order-history",
  GetHistoricalOrder = 'get-elephant-order',
  TrySubmitOrderDispute = 'try-submit-order-dispute',
  GetLatestRefund = 'get-latest-refund',
  SearchRefunds = 'search-refunds',
  SearchInvoices = 'search-invoices',
  GetProducerLocations = 'get-all-producer-locations',
  GetDailyReportsByProducerLocationKeyFolders = 'get-daily-reports-by-plk-folders',
  GetDailyReportsFolderForProducer = 'get-daily-reports-producer-folder',
  GetDailyReportDownloadUrl = 'get-download-url',
  GetCustomerData = 'get-customer-data',
  GetConsumerHeatmap = 'get-heatmap-consumer-data',
  GetOrderSources = 'get-order-sources',
  ToggleFoodTruckOperation = 'toggle-food-truck-operation',
  TryUpdateFoodTruckAddress = 'update-food-truck-address',
}

export const joinRoom = async (roomName: string, msg: object) => {
  try {
    await socketEmit(SocketRPC.JoinV2, {
      rooms: [{ ...msg, roomType: roomName }],
    });

    console.log(`> Joined socket room '${roomName}'`);
  } catch (err) {
    console.error(err);
  }
};

export const leaveRoom = async (roomName: string, msg: object) => {
  try {
    await socketEmit(SocketRPC.LeaveV2, {
      rooms: [{ ...msg, roomType: roomName }],
    });

    console.log(`> Left socket room '${roomName}'`);
  } catch (err) {
    console.error(err);
  }
};

const onConnect = async () => {
  let { user } = useUser.getState();

  const { setSocketConnected } = useSocketStatus.getState();

  if (!user) {
    user = getUserFromLocalStorage();
  }

  if (!user) {
    alert("Something went wrong...");
    throw new Error("Could not connect to socket, missing user..."); // TODO prompt to re login
  }

  setSocketConnected(true);
};

const onError = (err) => {
  console.log("> Generic socket error");
  console.error(err);
  if (err.type === "auth_error") {
    logout();
  }
};

const onConnectError = (err) => {
  console.log("> Socket connection error");
  console.error(err);
};

const onDisconnect = (err) => {
  console.log("> Socket disconnected");
  console.error(err);
};

const onInvalidToken = () => {
  console.log("Invalid token. Clearing localstorage");
  clearLocalStorage();
  window.location.href = "/";
};

const onOrderChange = (req) => {
  const { order } = req;
  const { orders, setOrders } = useOrders.getState();
  const newOrders = {
    ...orders,
    [order.orderKey]: order,
  };
  setOrders(newOrders);
};

const onDispatcherSettingsChange = (req) => {
  const { producerLocationKey, isFoodTruckOperationPaused } = req;
  const { producerLocations, setProducerLocations } = useProducerLocations.getState();
  const newProducerLocations = (producerLocations || []).map(location => {
    if (location.producerLocationKey === producerLocationKey) {
      return { ...location, isFoodTruckOperationPaused };
    }
    return {...location};
  });
  

  setProducerLocations(newProducerLocations);
};

const onActiveOrders = (req) => {
  const { orders } = req;
  const orderStore = {};
  const { setOrders, setOrdersLoaded } = useOrders.getState();
  orders.forEach((order) => {
    if (order) {
      orderStore[order.orderKey] = order;
    }
  });
  setOrders(orderStore);
  setOrdersLoaded(true);
};

const onRiderSnappedPing = (req) => {
  const { orderKey, deliveryKey, snappedPoint } = req;
  const { orders, setOrders } = useOrders.getState();

  orders[orderKey].deliveries.forEach((delivery) => {
    if (delivery.deliveryKey === deliveryKey) {
      delivery.lastPing = {
        longitude: snappedPoint.geometry.coordinates[0],
        latitude: snappedPoint.geometry.coordinates[1],
      };
    }
  });
  setOrders(orders);
};

const onJobFullPath = (req) => {
  const { featureCollection, orderKey, deliveryKey } = req;

  const coordinates = _.chain(featureCollection.features)
    .filter((feature) => {
      const deliveryKey = _.get(feature, 'properties.delivery-key');
      return !!deliveryKey;
    })
    .map('geometry.coordinates')
    .flatten()
    .value();

  const { orders, setOrders } = useOrders.getState();

  if (orders[orderKey]) {
    orders[orderKey].deliveries.forEach((delivery) => {
      if (delivery.deliveryKey === deliveryKey) {
        delivery.riderPath = {
          coordinates: coordinates.map((coordinate) => ({
            longitude: coordinate[0],
            latitude: coordinate[1],
          })),
        };
      }
    });
    setOrders(orders);
  }
};

const onJobRiderPings = (req) => {
  const { featureCollection, orderKey, deliveryKey } = req;

  const { orders, setOrders } = useOrders.getState();

  if (Object.keys(orders).length === 0) return;

  const coordinates = _.chain(featureCollection.features)
    .map('geometry.coordinates')
    .flatten()
    .value();

  if (orders[orderKey]) {
    orders[orderKey].deliveries.forEach((delivery) => {
      if (delivery.deliveryKey === deliveryKey) {
        delivery.lastPing = {
          longitude: coordinates[0],
          latitude: coordinates[1],
        };
      }
    });
    setOrders(orders);
  }

};

const onRiderEstimatedDropoff = (req) => {
  const { orderKey, deliveryKey, duration } = req;
  const { orders, setOrders } = useOrders.getState();

  const ETA = new Date(Date.now() + duration * 1000);

  orders[orderKey].deliveries.forEach((delivery) => {
    if (delivery.deliveryKey === deliveryKey) {
      delivery.estimatedTimeOfArrival = ETA;
    }
  });
  setOrders(orders);
};

export const openSocketConnection = (token?: string) => {
  console.log(`> Attempting socket connection`);
  socket.query.token = token ?? getTokenFromLocalStorage();
  const connection = socket.connect({ secure: true });

  bouncer = new Bouncer();
  bouncer.initialize({ connection });
};

export const closeSocketConnection = () => {
  if (socket.connected) {
    socket.close();
  }
};

interface joinOrderTrackingRoomProps {
  orderKey: string;
  refreshOrder?: boolean;
};
export const joinOrderTrackingRoom = async ({orderKey, refreshOrder = false}: joinOrderTrackingRoomProps) => {
  bouncer.join({
    roomType: "order-tracking",
    roomKey: `super_admin_order_tracking_${orderKey}`,
    orderKey,
    refreshOrder,
  });
};

export const leaveOrderTrackingRoom = async (orderKey: string) => {
  bouncer.leave({
    roomType: "order-tracking",
    roomKey: `super_admin_order_tracking_${orderKey}`,
    orderKey,
  });
};

export const joinRestaurantTrackingRoom = async (producerKey: string) => {
  bouncer.join({
    roomType: "plk-tracking",
    roomKey: `super_admin_plk_tracking_${producerKey}`,
    producerKey,
  });
};

export const leaveRestaurantTrackingRoom = async (producerKey: string) => {
  bouncer.leave({
    roomType: "plk-tracking",
    roomKey: `super_admin_plk_tracking_${producerKey}`,
    producerKey,
  });
};

export const socketEmit = (
  event: SocketEvent | SocketRPC,
  payload: any
): Promise<any> => {
  return new Promise((resolve, reject) => {
    socket.emit(event, payload, (res) => {
      if (res.success === false) {
        return reject(new Error(res.message ?? `${event} error`));
      }
      if (!res) {
        return reject(new Error(`${event} error`));
      }

      return resolve(res);
    });
  });
};

export const SocketEventHandlers = {
  [SocketEvent.Connect]: onConnect,
  [SocketEvent.Disconnect]: onDisconnect,
  [SocketEvent.Error]: onError,
  [SocketEvent.InvalidToken]: onInvalidToken,
  [SocketEvent.OrderChange]: onOrderChange,
  [SocketEvent.RestaurantDispatcherSettingsChanged]: onDispatcherSettingsChange,
  [SocketEvent.ActiveOrders]: onActiveOrders,
  [SocketEvent.ConnectError]: onConnectError,
  [SocketEvent.RiderSnappedPing]: onRiderSnappedPing,
  [SocketEvent.JobFullPath]: onJobFullPath,
  [SocketEvent.JobRiderPings]: onJobRiderPings,
  [SocketEvent.RiderEstimatedDropoff]: onRiderEstimatedDropoff,
};
